import { documentId, getDoc, getDocs, query, where } from 'firebase/firestore'
import { useEffect, useReducer, useState } from 'react'
import { Button, Form, InputGroup, Modal } from 'react-bootstrap'
import { FieldArrayMethodProps } from 'react-hook-form'
import { FaGlobe, FaLock, FaShareAlt, FaUserMinus } from 'react-icons/fa'
import { IoIosHelpCircle } from 'react-icons/io'
import { Profile, fb, profileRef, profilesRef } from '../../firebase'
import { chunkArray, emailPattern, logEvent } from '../../utils'
import { IconWrapper } from '../IconWrapper'
import { UserList } from '../user'

export type LimitationType = 'limited_by_url' | 'limited_by_uids'

export type LimitationTypeField = {
  value: LimitationType
  onChange: (...event: any[]) => void
}

export type AuthorizedUidsFieldArray = {
  fields: { value: string }[]
  append: (
    value:
      | Partial<{
          value: string
        }>
      | Partial<{
          value: string
        }>[],
    options?: FieldArrayMethodProps | undefined
  ) => void
  remove: (index?: number | number[] | undefined) => void
}

type Props = {
  title: string
  help?: string
  helpEvent?: string
  show: boolean
  onHide: () => void
  sharedPath: string
  authorizedUidsFieldArray: AuthorizedUidsFieldArray
  limitationTypeField: LimitationTypeField
}

export const LimitedAuthorizationSetting = ({
  title,
  help,
  helpEvent,
  show,
  onHide: handleClose,
  sharedPath,
  authorizedUidsFieldArray,
  limitationTypeField,
}: Props) => {
  const [visibleUser, setVisibleUser] = useState('')
  const [isInitialize, updateIsInitialize] = useReducer(() => true, false)
  const addVisibleUser = async (text: string) => {
    const uid = emailPattern.test(text)
      ? (await fb.call('getUidByEmail')({ email: text })).data.uid
      : text
    const profile = await getDoc(profileRef(uid))
    if (!profile.exists()) {
      alert('存在しないユーザIDです。')
      return
    }
    if (visibleUserProfiles.some((p) => p.id === profile.id)) {
      alert('既に追加されています。')
      return
    }
    setVisibleUserProfiles([...visibleUserProfiles, profile.data()])
    authorizedUidsFieldArray.append({ value: profile.id })
  }

  const deleteVisibleUser = (id: string) => {
    setVisibleUserProfiles(
      visibleUserProfiles.filter((profile) => profile.id !== id)
    )
  }

  const [visibleUserProfiles, setVisibleUserProfiles] = useState<Profile[]>([])

  useEffect(() => {
    if (isInitialize) return
    /**
     * 記事を既に共有されているユーザのProfileデータを取得してauthorizedUidsに追加する
     */
    const getInitialVisibleUsers = async () => {
      const users = authorizedUidsFieldArray.fields.map((u) => u.value)
      if (users === undefined || users.length === 0) return
      const queries = chunkArray(users, 10).map((usersChunk) =>
        getDocs(query(profilesRef, where(documentId(), 'in', usersChunk)))
      )
      const profilesList = await Promise.all(queries)
      const visibleUsersTmp: Profile[] = []
      profilesList.map((profiles) =>
        profiles.forEach((p) => {
          if (!p.exists()) return
          visibleUsersTmp.push(p.data())
        })
      )
      setVisibleUserProfiles(visibleUsersTmp)
      updateIsInitialize()
    }
    void getInitialVisibleUsers()
  }, [authorizedUidsFieldArray.fields])

  return (
    <Modal
      size="lg"
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      scrollable={true}
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">
          {title}
          {help !== undefined && (
            <a href={help} target="_blank">
              <IoIosHelpCircle
                className="ms-1 cursor-pointer text-black"
                onClick={() => {
                  if (helpEvent !== undefined) logEvent(helpEvent)
                }}
              />
            </a>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <InputGroup>
            <IconWrapper className="me-3 fs-5">
              {limitationTypeField.value === 'limited_by_url' ? (
                <FaGlobe />
              ) : (
                <FaLock />
              )}
            </IconWrapper>
            <Form.Select
              size="sm"
              onChange={limitationTypeField.onChange}
              value={limitationTypeField.value}
            >
              <option value="limited_by_uids">制限付き</option>
              <option value="limited_by_url">リンクを知っている全員</option>
            </Form.Select>
          </InputGroup>
          <div className="mt-2">
            {limitationTypeField.value === 'limited_by_uids'
              ? '権限を持つユーザのみがアクセスできます。'
              : 'リンクを知っているインターネット上の全員がアクセスできます。'}
          </div>
          <InputGroup className="mt-5">
            <Form.Control
              size="sm"
              value={visibleUser}
              placeholder="ユーザのメールアドレスまたはIDを入力してください"
              onChange={(e) => {
                setVisibleUser(e.currentTarget.value)
              }}
            />
            <Button
              size="sm"
              onClick={async () => {
                await addVisibleUser(visibleUser)
                setVisibleUser('')
              }}
              disabled={visibleUser === ''}
            >
              追加
            </Button>
          </InputGroup>
          <div className="py-2">
            <UserList
              className="gap-3 d-flex flex-column"
              profiles={visibleUserProfiles}
              isFollowButton={false}
              suffix={({ profile, index }) => (
                <Button
                  variant={'outline-danger'}
                  onClick={() => {
                    authorizedUidsFieldArray.remove(index)
                    deleteVisibleUser(profile.id)
                  }}
                >
                  <FaUserMinus />
                </Button>
              )}
            />
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button
          size="sm"
          variant="secondary"
          onClick={async () => {
            if (process.env.NEXT_PUBLIC_HOST === undefined) {
              throw Error('missing NEXT_PUBLIC_HOST.')
            }
            await navigator.clipboard.writeText(
              `${process.env.NEXT_PUBLIC_HOST}${sharedPath}`
            )
            alert('URLをコピーしました。')
          }}
        >
          <IconWrapper suffix="URLをコピー">
            <FaShareAlt />
          </IconWrapper>
        </Button>
        <Button size="sm" onClick={handleClose}>
          完了
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
